import clsx from 'clsx';
import { Check } from 'lucide-react';
import React from 'react';

export interface RadioCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  active: boolean;
  disabled?: boolean;
  onSelect: () => void;
  className?: string;
}

/**
 * Selectable 2-up card control for small exclusive choices with an icon and
 * subtitle (e.g. Pool Style, Sanitation Type). Not a native radio input —
 * use RadioCardGroup or manage active/onSelect per-option like a radio group.
 */
const RadioCard = ({
  icon,
  title,
  subtitle,
  active,
  disabled,
  onSelect,
  className,
}: RadioCardProps) => (
  <button
    type="button"
    onClick={onSelect}
    disabled={disabled}
    aria-pressed={active}
    className={clsx(
      'flex items-start gap-3 rounded-[14px] border-[1.5px] p-4 text-left transition-colors',
      'focus-visible:outline-none focus-visible:border-primary',
      disabled && 'pointer-events-none opacity-50',
      active
        ? 'border-primary bg-cloud'
        : 'border-grey bg-white hover:border-secondary',
      className
    )}
  >
    <span
      className={clsx(
        'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[11px] [&>svg]:h-5 [&>svg]:w-5',
        active ? 'bg-primary text-white' : 'bg-cloud text-primary'
      )}
    >
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-bold text-textDark">{title}</span>
      {subtitle && (
        <span className="mt-0.5 block text-[12.5px] leading-snug text-textLight">
          {subtitle}
        </span>
      )}
    </span>
    <span
      className={clsx(
        'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 text-white',
        active ? 'border-primary bg-primary' : 'border-grey'
      )}
    >
      {active && <Check className="h-3 w-3" strokeWidth={3} />}
    </span>
  </button>
);

export default RadioCard;
